import {
  PDFViewer,
  Document,
  StyleSheet,
  Page,
  View,
  Text,
  Font,
} from '@react-pdf/renderer'
import React, { useEffect, useMemo, useState } from 'react'
//@ts-ignore
import font from '../../../assets/fonts/OpenSauceSans-Bold.ttf'
//@ts-ignore
import fontRegular from '../../../assets/fonts/OpenSauceSans-Regular.ttf'
import PdfView from '../../../components/Display/PdfView'
import { formatPipe } from '../../../utils/utils'
import { DateTime } from 'luxon'
import { toFixed } from 'accounting'

const SalesBook: React.FC<any> = ({
  salesBook,
  infoAccount,
  dateFrom,
  dateTo,
  thermalPrinter
}) => {
  Font.register({
    family: 'Open Sans',
    fonts: [{ src: font }],
  })

  Font.register({
    family: 'Open Sans-normal',
    fonts: [{ src: fontRegular }],
  })

  const styles = StyleSheet.create({
    headerRow: {
      flexDirection: 'row',
      width: '100%',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 10,
    },
    leftCol: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexGrow: 1,
    },
    centerCol: {
      flexDirection: 'column',
      alignItems: 'center',
      flexGrow: 2,
    },
    rightCol: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      flexGrow: 1,
    },
    businessName: {
      fontFamily: 'Open Sans',
      fontSize: 22,
      fontWeight: 900,
    },
    normalText: {
      fontFamily: 'Open Sans-normal',
      fontSize: 12,
    },
    title: {
      fontFamily: 'Open Sans',
      fontSize: 18,
      fontWeight: 700,
      marginBottom: 2,
    },
    dateText: {
      fontFamily: 'Open Sans-normal',
      fontSize: 12,
      marginBottom: 2,
    },
    tableHeaderRow: {
      flexDirection: 'row',
      marginTop: 10,
      marginBottom: 2,
      marginLeft: 20,
    },
    tableHeaderCell: {
      border: '1px solid #000',
      fontFamily: 'Open Sans-normal',
      fontSize: 12,
      padding: 4,
      textAlign: 'center',
      flexGrow: 1,
    },
    tableHeaderCellDouble: {
      border: '1px solid #000',
      fontFamily: 'Open Sans-normal',
      fontSize: 12,
      padding: 4,
      textAlign: 'center',
      flexGrow: 2,
    },
  })

  const data = salesBook?.sales?.map((x: any, idx: number) => ({
    nroOpe: idx + 1,
    fecha: DateTime.fromISO(x?.date).toFormat('dd/MM/yy'),
    idFiscal: x?.clientDocument || '',
    nombreRazonSocial: x?.clientName || '',
    thermalPrinter: thermalPrinter?.registerNumber || '',
    numeroFactura: x?.invoiceCode || '',
    numeroDocum: x?.invoiceControl || '',
    totalVentasConImpuestos: formatPipe(x?.totalSaleWithTaxes) || '',
    ventasExentas: formatPipe(x?.excemptSale) || '',
    montoAlicGralBaseImponible: formatPipe(x?.generalIVAtaxableBase) || '',
    porcentajeAlicIva: '16%',
    montoIvaAlicGral: formatPipe(x?.generalIVAtax) || '',
    baseRetencionIva: formatPipe(x?.ivaTaxableBase) || '',
    ivaRetenido: formatPipe(x?.ivaWithholding) || '',
    ivaPercibido: formatPipe(x?.ivaPerceived) || '',
    status: x?.cancelled ? 'Anulada' : '',
  }))

  const totals = useMemo(() => {
    if (!data) return {
        totalVentasConImpuestos: '0.00',
        ventasExentas: '0.00',
        montoAlicGralBaseImponible: '0.00',
        montoIvaAlicGral: '0.00',
        baseRetencionIva: '0.00',
        ivaRetenido: '0.00',
        ivaPercibido: '0.00',
    }

    return {
      totalVentasConImpuestos: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.totalVentasConImpuestos, true), 0), 2),
      ventasExentas: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.ventasExentas, true), 0), 2),
      montoAlicGralBaseImponible: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.montoAlicGralBaseImponible, true), 0), 2),
      montoIvaAlicGral: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.montoIvaAlicGral, true), 0), 2),
      baseRetencionIva: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.baseRetencionIva, true), 0), 2),
      ivaRetenido: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.ivaRetenido, true), 0), 2),
      ivaPercibido: toFixed(data.reduce((acc, curr) => acc + +formatPipe(curr.ivaPercibido, true), 0), 2),
    }
  }, [data])

  // Define las columnas una sola vez:
  const localColumns = [
    { key: 'nroOpe', label: 'Nro Ope', width: 20 },
    { key: 'fecha', label: 'Fecha', width: 30 },
    { key: 'idFiscal', label: 'ID Fiscal', width: 50 },
    { key: 'nombreRazonSocial', label: 'Nombre o razón social', width: 200 },
    { key: 'status', label: 'Estado', width: 40 },
    { key: 'thermalPrinter', label: 'Nro Reg Maq.', width: 50 },
    { key: 'numeroFactura', label: 'Nro Factura', width: 50 },
    { key: 'numeroDocum', label: 'Nro Documento', width: 50 },
    { key: 'totalVentasConImpuestos', label: 'Total Venta con Impuestos', width: 100 },
    { key: 'ventasExentas', label: 'Ventas Exentas', width: 70 },
    { key: 'montoAlicGralBaseImponible', label: 'Alic. Gral. Base Imponible', width: 90 },
    { key: 'porcentajeAlicIva', label: '% Alic IVA', width: 30 },
    { key: 'montoIvaAlicGral', label: 'Monto IVA Alic Gral', width: 70 },
    { key: 'baseRetencionIva', label: 'Base Retención', width: 70 },
    { key: 'ivaRetenido', label: 'IVA Retenido', width: 70 },
    { key: 'ivaPercibido', label: 'IVA Percibido', width: 70 },
  ]

  // const [columns, setColumns] = useState(localColumns)
  

  // useEffect(() => {
  //   if (infoAccount?.invoiceMethod !== "thermal_printer") {
  //     setColumns(localColumns.filter((col) => col.key !== 'thermalPrinter'))
  //   } 
  // }, [thermalPrinter])

  const columns = useMemo(() => {
    if (infoAccount?.invoiceMethod !== "thermal_printer") {
      return localColumns.filter((col) => col.key !== 'thermalPrinter')
    }
    return localColumns
  }, [infoAccount?.invoiceMethod])

  return (
    <Document title={`Libro_Ventas`}>
      <Page size="A2" orientation="landscape" style={{ padding: 30 }}>
        {/* Header Primera fila */}
        <View style={styles.headerRow} fixed>
          {/* Left */}
          <View style={styles.leftCol}>
            <Text style={styles.businessName}>{infoAccount?.businessName}</Text>
            <Text style={styles.normalText}>{infoAccount?.address?.address}</Text>
            <Text style={styles.normalText}>{infoAccount?.businessSocial}</Text>
          </View>
          {/* Center */}

          {/* Right */}
          <View style={styles.rightCol}>
            <Text
              style={styles.normalText}
              render={({ pageNumber, totalPages }) =>
                `Página ${pageNumber} de ${totalPages}`
              }
            />
            <Text style={styles.normalText} fixed>
              {`${DateTime.now().toFormat('dd/MM/yyyy')}`}
            </Text>
          </View>
        </View>

        {/* Header Segunda fila */}
        <View style={{marginTop: -20}} fixed>
          {/** Titulo */}
          <View style={styles.centerCol}>

            <Text style={styles.title}>Libro de ventas I.V.A.</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={styles.dateText}>desde : {dateFrom}</Text>
              <Text style={styles.dateText}> hasta {dateTo}</Text>
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {/** Recuadro */}
            <View style={styles.tableHeaderRow}>
              <View
                style={{ flexDirection: 'column', border: '1px solid #000', marginLeft: 950 }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #000',
                    padding: 4,
                    width: 300
                  }}
                >
                  <Text style={{ fontSize: 12, fontWeight: 700, textAlign: 'left' }}>
                    Ventas Internas a Contribuyentes
                  </Text>
                </View>

                <View style={{ padding: 4, flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center', }}>
                  <Text style={{ fontSize: 12, fontWeight: 700 }}>
                    Ventas por alic. general (16%)
                  </Text>
                </View>
              </View>

              {/* <View
                style={{
                  flexDirection: 'column',
                  border: '1px solid #000',
                  marginLeft: 4,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #000',
                    padding: 4,
                  }}
                >
                  <Text style={{ fontSize: 12, fontWeight: 700 }}>
                    Ventas Internas a No Contribuyentes
                  </Text>
                </View>

                <View style={{ padding: 4 }}>
                  <Text style={{ fontSize: 12, fontWeight: 700 }}>
                    Ventas por alic. general (16%)
                  </Text>
                </View>
              </View> */}
            </View>

            <View style={{flexDirection: 'column', marginLeft: 35, width: 310}}>
              <Text style={{ fontSize: 12, fontWeight: 700, textAlign: 'center' }}>Retenciones</Text>
              <View style={{height: 1, width: "100%", backgroundColor: '#000', marginTop: 3}}></View>
            </View> 
          </View>

          </View>
          
          
        </View>

        {/* Tabla columnas */}
        <View style={{ marginTop: 20 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {columns.map((col) => (
              <View key={col.key} style={{ width: col.width, padding: 2 }}>
                <Text style={{ fontSize: 9, textAlign: 'left' }} wrap>
                  {col.label}
                </Text>
              </View>
            ))}
          </View>

          <View
            style={{
              height: 1,
              width: '100%',
              backgroundColor: '#000',
              marginTop: 1,
            }}
          ></View>
        </View>

        {/* Tabla datos */}
        <View>
          {data &&
            data.map((row, idx) => (
              <View
                key={idx}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {columns.map((col) => (
                  <View key={col.key} style={{ width: col.width, padding: 2 }}>
                    <Text style={{ fontSize: 9, textAlign: 'left' }}>
                      {row[col.key]}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
        </View>

        {/** Footer de sumas dentro de tabla */}
        <View style={{ marginTop: 5 }}>
          <View
            style={{
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <View
              style={{ width: '58%', height: 1, backgroundColor: '#000' }}
            ></View>
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {columns.map((col, idx) => (
              <View key={col.key} style={{ width: col.width, padding: 2 }}>
                {idx >= 6 && (
                  <Text style={{ fontSize: 9, textAlign: 'left' }} wrap>
                    {formatPipe(totals[col.key] || '')}
                  </Text>
                )}
              </View>
            ))}
          </View>
        </View>

        {/** Resumen de debitos fiscales */}
        <View style={{ marginTop: 40, flexDirection: 'row', width: '40%' }}>
          {/* Columna izquierda */}
          <View style={{ flex: 2 }}>
            <Text style={{ fontSize: 14, fontWeight: 700, fontFamily: 'Open Sans', marginBottom: 4 }}>
              Resumen de Debitos Fiscales
            </Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>Retenciones Aplicadas</Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>
            Ventas Internas gravadas por alicuota general (16%)
            </Text>
            
            <Text style={{ fontSize: 9, fontWeight: 700, fontFamily: 'Open Sans', marginTop: 8 }}>
              Total ventas y débitos fiscales del período
            </Text>

          </View>
          {/* Columna Base Imponible */}
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 14, fontWeight: 700, fontFamily: 'Open Sans', marginBottom: 4 }}>
              Base Imponible
            </Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>{formatPipe(toFixed(+salesBook?.totalWithholdingTaxableBase, 2))}</Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>{formatPipe(toFixed(+salesBook?.totalGeneralIVAtaxableBase, 2))}</Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans', marginTop: 8 }}>{formatPipe(toFixed(+salesBook?.totalGeneralIVAtaxableBase, 2))}</Text>
            
          </View>
          {/* Columna Debito Fiscal */}
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 14, fontWeight: 700, fontFamily: 'Open Sans', marginBottom: 4 }}>
              Debito Fiscal
            </Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>{formatPipe(toFixed(+salesBook?.totalIvaPerceived, 2))}</Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans-normal' }}>{formatPipe(toFixed(+salesBook?.totalGeneralIVAtax, 2))}</Text>
            <Text style={{ fontSize: 9, fontFamily: 'Open Sans', marginTop: 8 }}>{formatPipe(toFixed(+salesBook?.totalGeneralIVAtax - +salesBook?.totalWithholding, 2))}</Text>
          
          </View>
        </View>
      </Page>
    </Document>
  )
}

export interface ISalesBook {
  data: any[]
  dateFrom: string
  dateTo: string
  businessSocial: string
  businessDocument: string
  parentCode: string
  parentName: string
  parentBalance: string
  parentBeforeBalance: string
  accountName: string
  accountCode: string
}

export default SalesBook
